import { Fragment, useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { formatApiErrorDetail } from "@/lib/errors";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { BatchReview } from "@/components/catalog/BatchReview";
import { ArrowLeft, ChevronUp, Download, Eye, Loader2, RotateCcw, Send, Trash2, Upload } from "lucide-react";

const STATUS_BADGE = {
  DRAFT: "badge-reserved",
  APPROVED: "badge-available",
  RETIRED: "badge-sold",
};
const CORE_FIELDS = ["moroccan_name", "approved_us_name", "primary_family", "secondary_family"];
const LIST_FIELDS = [
  ["approved_aliases", "Approved aliases (comma-separated)"],
  ["approved_descriptors", "Approved descriptors (comma-separated)"],
  ["rejected_descriptors", "Rejected descriptors (comma-separated)"],
];
const TEXT_FIELDS = [
  ["provenance", "Source / provenance"],
  ["evidence", "Evidence"],
  ["internal_notes", "Internal notes (Admin-only)"],
];
const FIELD_LABELS = {
  moroccan_name: "Moroccan / factory name", approved_us_name: "Approved U.S. display name",
  primary_family: "Primary family", secondary_family: "Secondary family / undertone",
};

const toList = (s) => s.split(",").map((x) => x.trim()).filter(Boolean);
const fromList = (v) => (Array.isArray(v) ? v.join(", ") : "");

export default function AdminColorGovernance() {
  const [rows, setRows] = useState(null);
  const [sel, setSel] = useState(null); // detail payload
  const [form, setForm] = useState(null);
  const [preview, setPreview] = useState(null);
  const [validation, setValidation] = useState(null);
  const [busy, setBusy] = useState("");
  const [rollbackRev, setRollbackRev] = useState("");
  const [diag, setDiag] = useState(null); // legacy migration diagnostic (audit-only)

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/catalog/governance");
      setRows(data);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const open = async (code) => {
    try {
      const { data } = await api.get(`/admin/catalog/governance/${code}`);
      setSel(data);
      const c = data.color;
      // CAT-169: a legacy migration diagnostic is not an approved source — keep it
      // out of the editable field; it remains visible under the audit panel below.
      setDiag((c.provenance || "").startsWith("UNRESOLVED —") ? c.provenance : null);
      setForm({
        moroccan_name: c.moroccan_name || "", approved_us_name: c.approved_us_name || "",
        primary_family: c.primary_family || "", secondary_family: c.secondary_family || "",
        approved_aliases: fromList(c.approved_aliases), approved_descriptors: fromList(c.approved_descriptors),
        rejected_descriptors: fromList(c.rejected_descriptors),
        provenance: (c.provenance || "").startsWith("UNRESOLVED —") ? "" : (c.provenance || ""),
        evidence: c.evidence || "", internal_notes: c.internal_notes || "",
        evidence_tech_doc: c.evidence_tech_doc || "", evidence_media_id: c.evidence_media_id || "",
      });
      setPreview(null); setValidation(null); setRollbackRev("");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  // RC48-COL-002/003: collapse in place; clicking another row switches directly
  const toggle = (code) => { if (sel?.color.code === code) setSel(null); else open(code); };

  const act = async (kind, fn, msg) => {
    setBusy(kind);
    try {
      await fn();
      toast.success(msg);
      await load();
      if (sel) await open(sel.color.code);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(""); }
  };

  const draftPayload = () => ({
    ...form,
    approved_aliases: toList(form.approved_aliases),
    approved_descriptors: toList(form.approved_descriptors),
    rejected_descriptors: toList(form.rejected_descriptors),
    note: "Admin Color Governance UI",
  });

  const saveDraft = () => act("draft", async () => {
    await api.put(`/admin/catalog/governance/${sel.color.code}/draft`, draftPayload());
  }, "Draft saved — not live until published");

  const publish = () => act("publish", async () => {
    // V4.7 CAT-166: Approve & publish carries the exact editor values — the server
    // validates fail-closed and persists them atomically with the publication.
    await api.post(`/admin/catalog/governance/${sel.color.code}/publish`, draftPayload());
  }, "Published — Catalog, search and ZELI now use these exact values");

  const validate = async () => {
    setBusy("validate");
    try {
      const { data } = await api.post(`/admin/catalog/governance/${sel.color.code}/validate`);
      setValidation(data);
      // CAT-168: "Validation complete" is never shown for an invalid record
      if (data.valid) toast.success("Validation complete — all publish requirements passed");
      else toast.error(`Cannot publish yet — ${data.errors[0]}`);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(""); }
  };

  const retire = () => {
    // CAT-172 / UI-160: name the real consequence — retire is not delete
    if (!window.confirm(`Retire ${sel.color.code} — ${sel.color.name}${sel.color.approved_us_name ? ` · ${sel.color.approved_us_name}` : ""}?\n\nThe mapping is removed from the Dealer catalog, search and ZELI. Nothing is deleted: the complete revision history is preserved, and the mapping can be restored later with Roll back & republish.`)) return;
    act("retire", async () => {
      await api.post(`/admin/catalog/governance/${sel.color.code}/retire`, { note: "Admin Color Governance UI" });
    }, "Mapping retired — removed from Catalog, search and ZELI; history preserved");
  };

  const rollback = () => act("rollback", async () => {
    await api.post(`/admin/catalog/governance/${sel.color.code}/rollback`, { revision: Number(rollbackRev), note: "Admin Color Governance UI" });
  }, `Rolled back to revision ${rollbackRev} and republished`);

  const loadPreview = () => act("preview", async () => {
    const { data } = await api.get(`/admin/catalog/governance/${sel.color.code}/preview`);
    setPreview(data);
  }, "Preview refreshed");

  const uploadEvidence = async (file) => {
    if (!file || !sel) return;
    setBusy("evidence");
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post(`/admin/catalog/governance/${sel.color.code}/evidence-file`, fd);
      toast.success("Evidence file attached to this color record");
      await open(sel.color.code);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(""); }
  };

  if (!rows) {
    return <div className="flex min-h-[40vh] items-center justify-center" data-testid="color-governance-loading"><Loader2 className="h-6 w-6 animate-spin text-terracotta" /></div>;
  }

  // RC48-COL-001: the editor renders inline, directly beneath the clicked row
  const renderEditor = () => (
    <div className="space-y-5 rounded-md border border-border bg-white p-6 text-left" data-testid="gov-editor">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="font-display text-2xl font-light text-charcoal" data-testid="gov-editor-title">
            {sel.color.code} — {sel.color.moroccan_name || sel.color.name}
            {sel.color.governance_status === "APPROVED" && sel.color.approved_us_name
              ? <span className="ml-2 text-terracotta" data-testid="gov-editor-us-name">· {sel.color.approved_us_name}</span>
              : <span className="ml-2 align-middle badge-status badge-reserved" data-testid="gov-editor-us-pending">U.S. name pending</span>}
          </h2>
          <p className="mt-1 font-mono text-[11px] text-muted-foreground">
            {sel.color.format_skus ? Object.entries(sel.color.format_skus).map(([f, s]) => `${s} (${f})`).join(" · ") : ""}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <span className={`rounded-full px-2.5 py-1 text-[10px] font-semibold uppercase ${STATUS_BADGE[sel.color.governance_status] || "badge-reserved"}`}
            data-testid="gov-editor-status">{sel.color.governance_status}</span>
          <Button size="sm" variant="outline" onClick={() => setSel(null)} className="border-limestone" data-testid="gov-collapse">
            <ChevronUp className="mr-1 h-3.5 w-3.5" />Collapse
          </Button>
        </div>
      </div>

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        <div>
          <label className="text-xs font-semibold uppercase tracking-widest text-warmgrey">Color number</label>
          <Input value={sel.color.code} disabled className="mt-1 h-10 bg-sand font-mono" data-testid="gov-field-code" />
        </div>
        {CORE_FIELDS.map((f) => (
          <div key={f}>
            <label className="text-xs font-semibold uppercase tracking-widest text-warmgrey">{FIELD_LABELS[f]}</label>
            <Input value={form[f]} onChange={(e) => setForm({ ...form, [f]: e.target.value })} className="mt-1 h-10 bg-white focus-visible:ring-terracotta" data-testid={`gov-field-${f.replaceAll("_", "-")}`} />
            {!form[f] && <p className="mt-1 text-[10px] italic text-warmgrey" data-testid={`gov-unresolved-flag-${f.replaceAll("_", "-")}`}>Unresolved — never guessed or published</p>}
          </div>
        ))}
      </div>

      <div className="grid gap-4 lg:grid-cols-3">
        {LIST_FIELDS.map(([f, label]) => (
          <div key={f}>
            <label className="text-xs font-semibold uppercase tracking-widest text-warmgrey">{label}</label>
            <Input value={form[f]} onChange={(e) => setForm({ ...form, [f]: e.target.value })} className="mt-1 h-10 bg-white focus-visible:ring-terracotta" data-testid={`gov-field-${f.replaceAll("_", "-")}`} />
          </div>
        ))}
      </div>
      <div className="grid gap-4 lg:grid-cols-3">
        {TEXT_FIELDS.map(([f, label]) => (
          <div key={f}>
            <label className="text-xs font-semibold uppercase tracking-widest text-warmgrey">{label}</label>
            {f === "provenance" && (
              <p className="mt-0.5 text-[11px] text-warmgrey" data-testid="gov-help-provenance">Where the approved value came from — e.g. “Founder approval, 28 Sep 2026”, a named catalog file, or a supplier document.</p>
            )}
            {f === "evidence" && (
              <p className="mt-0.5 text-[11px] text-warmgrey" data-testid="gov-help-evidence">The specific support for the value — e.g. a file, spreadsheet row, approval note or document section.</p>
            )}
            <Textarea value={form[f]} onChange={(e) => setForm({ ...form, [f]: e.target.value })} rows={2} className="mt-1 bg-white focus-visible:ring-terracotta" data-testid={`gov-field-${f.replaceAll("_", "-")}`} />
          </div>
        ))}
      </div>

      {/* RC48-COL-016/017/018: evidence may be an actual file, a governed technical
          document, or a Media Library asset — not only free text */}
      <div className="grid gap-4 rounded-md border border-dashed border-limestone p-4 lg:grid-cols-3" data-testid="gov-evidence-associations">
        <div>
          <label className="text-xs font-semibold uppercase tracking-widest text-warmgrey">Evidence file or photo</label>
          {sel.color.evidence_file?.file_name ? (
            <div className="mt-1 flex items-center gap-2 text-xs" data-testid="gov-evidence-file-attached">
              <a href={`${api.defaults.baseURL}/admin/catalog/governance/${sel.color.code}/evidence-file`}
                target="_blank" rel="noreferrer" className="flex items-center gap-1 text-terracotta hover:underline" data-testid="gov-evidence-file-open">
                <Download className="h-3.5 w-3.5" />{sel.color.evidence_file.file_name}
              </a>
              <span className="text-warmgrey">· replaces on new upload</span>
            </div>
          ) : (
            <p className="mt-1 text-[11px] text-warmgrey">No file attached</p>
          )}
          <Input type="file" accept=".pdf,.doc,.docx,.xls,.xlsx,.txt,.csv,.png,.jpg,.jpeg,.webp"
            onChange={(e) => uploadEvidence(e.target.files?.[0])} className="mt-2 h-10 bg-white" data-testid="gov-evidence-file-upload" />
        </div>
        <div>
          <label className="text-xs font-semibold uppercase tracking-widest text-warmgrey">Technical document link</label>
          <Input value={form.evidence_tech_doc} onChange={(e) => setForm({ ...form, evidence_tech_doc: e.target.value })}
            placeholder="e.g. MZ-TDS-001" className="mt-1 h-10 bg-white" data-testid="gov-field-evidence-tech-doc" />
          <p className="mt-1 text-[11px] text-warmgrey">A governed document number from Technical Knowledge.</p>
        </div>
        <div>
          <label className="text-xs font-semibold uppercase tracking-widest text-warmgrey">Media Library evidence link</label>
          <Input value={form.evidence_media_id} onChange={(e) => setForm({ ...form, evidence_media_id: e.target.value })}
            placeholder="Media asset ID" className="mt-1 h-10 bg-white" data-testid="gov-field-evidence-media-id" />
          <p className="mt-1 text-[11px] text-warmgrey">An asset from the Media Library supporting this mapping.</p>
        </div>
      </div>

      {sel.unresolved.length > 0 && (
        <p className="rounded-md border border-dashed border-limestone bg-sand px-3 py-2 text-xs text-warmgrey" data-testid="gov-unresolved-summary">
          {sel.unresolved.length} field{sel.unresolved.length > 1 ? "s" : ""} still need{sel.unresolved.length === 1 ? "s" : ""} a value
          ({sel.unresolved.map((f) => FIELD_LABELS[f] || f).join(", ")}).
          Unresolved fields are never guessed, and they never publish.
        </p>
      )}

      <div className="flex flex-wrap gap-2 border-t border-border pt-4">
        <Button onClick={saveDraft} disabled={!!busy} variant="outline" className="border-limestone" data-testid="gov-save-draft">
          {busy === "draft" && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}Save draft
        </Button>
        <Button onClick={validate} disabled={!!busy} variant="outline" className="border-limestone" data-testid="gov-validate">
          {busy === "validate" && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}Validate
        </Button>
        <Button onClick={publish} disabled={!!busy} className="bg-terracotta text-white hover:bg-terracotta/90" data-testid="gov-publish">
          {busy === "publish" ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Send className="mr-2 h-4 w-4" />}Approve &amp; publish
        </Button>
        <Button onClick={retire} disabled={!!busy || sel.color.governance_status === "RETIRED"} variant="outline" className="border-status-sold text-status-sold" data-testid="gov-retire">
          <Trash2 className="mr-2 h-4 w-4" />Retire
        </Button>
        <Button onClick={loadPreview} disabled={!!busy} variant="outline" className="border-limestone" data-testid="gov-preview-button">
          <Eye className="mr-2 h-4 w-4" />Preview dealer/ZELI
        </Button>
      </div>

      {validation && (
        <div className="rounded-md border border-border bg-sand p-4 text-sm" data-testid="gov-validation-result">
          <p className="font-semibold text-charcoal">{validation.valid ? "Valid — publishable" : "Not publishable"}</p>
          {validation.errors.map((e) => <p key={e} className="mt-1 text-status-sold" data-testid="gov-validation-error">{e}</p>)}
          {validation.unresolved.length > 0 && (
            <p className="mt-1 text-warmgrey" data-testid="gov-validation-unresolved">
              Unresolved (allowed, excluded from publication): {validation.unresolved.join(", ")}
            </p>
          )}
        </div>
      )}

      {preview && (
        <div className="rounded-md border border-border bg-sand p-4 text-sm" data-testid="gov-preview">
          <p className="text-xs font-semibold uppercase tracking-widest text-warmgrey">Dealer / ZELI interpretation</p>
          <div className="mt-2 grid gap-3 sm:grid-cols-2">
            <div data-testid="gov-preview-current">
              <p className="text-xs font-semibold text-charcoal">Live now</p>
              <p className="mt-1">Display name: <span className="font-medium">{preview.current.display_name}</span></p>
              <p className="mt-1 text-xs text-warmgrey">Aliases active: {preview.current.aliases_active.length ? preview.current.aliases_active.join(", ") : "none"}</p>
            </div>
            <div data-testid="gov-preview-if-published">
              <p className="text-xs font-semibold text-charcoal">If published now</p>
              <p className="mt-1">Display name: <span className="font-medium">{preview.if_published.display_name}</span></p>
              <p className="mt-1 text-xs text-warmgrey">Aliases active: {preview.if_published.aliases_active.length ? preview.if_published.aliases_active.join(", ") : "none"}</p>
            </div>
          </div>
        </div>
      )}

      <div className="flex flex-wrap items-center gap-2 border-t border-border pt-4">
        {(() => {
          const eligible = (sel.revisions || []).filter((r) => (r.action === "publish" || r.action === "rollback" || r.action === "restore") && !r.invalidated);
          return eligible.length === 0 ? (
            <p className="text-xs text-warmgrey" data-testid="gov-rollback-unavailable">
              No earlier published revision is available to roll back to. Rollback restores a previous published revision; governed history is never deleted.
            </p>
          ) : (
            <>
              <Select value={rollbackRev} onValueChange={setRollbackRev}>
                <SelectTrigger className="h-10 w-44 bg-white" data-testid="gov-rollback-select"><SelectValue placeholder="Revision…" /></SelectTrigger>
                <SelectContent className="bg-white">
                  {eligible.map((r) => (
                    <SelectItem key={r.revision} value={String(r.revision)} data-testid={`gov-rollback-option-${r.revision}`}>Revision {r.revision}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Button onClick={rollback} disabled={!!busy || !rollbackRev} variant="outline" className="border-limestone" data-testid="gov-rollback">
                <RotateCcw className="mr-2 h-4 w-4" />Roll back &amp; republish
              </Button>
            </>
          );
        })()}
      </div>

      <div className="border-t border-border pt-4" data-testid="gov-history">
        <p className="text-xs font-semibold uppercase tracking-widest text-warmgrey">Publication history &amp; audit</p>
        {diag && (
          <details className="mt-2 rounded-md border border-border px-3 py-2 text-xs" data-testid="gov-system-diagnostic">
            <summary className="cursor-pointer text-warmgrey">System diagnostic (migration note — not an approved source)</summary>
            <p className="mt-1 text-warmgrey">{diag}</p>
          </details>
        )}
        {(sel.revisions || []).length === 0 ? (
          <p className="mt-2 text-sm text-warmgrey">No publication events yet.</p>
        ) : (
          <ul className="mt-2 space-y-2">
            {sel.revisions.map((r) => (
              <li key={`${r.revision}-${r.action}-${r.at}`} className="rounded-md border border-border px-3 py-2 text-xs" data-testid={`gov-history-${r.revision}-${r.action}`}>
                <span className="font-mono font-semibold">rev {r.revision}</span> · {r.action} · {r.actor} · {r.at}
                {r.restored_from ? ` · restored from rev ${r.restored_from}` : ""}{r.note ? ` — ${r.note}` : ""}
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );

  return (
    <div data-testid="color-governance-page">
      <Link to="/admin/catalog" className="inline-flex items-center gap-1 text-xs text-warmgrey hover:text-terracotta" data-testid="back-to-catalog">
        <ArrowLeft className="h-3 w-3" /> Colors &amp; variants
      </Link>
      <p className="mt-3 text-xs font-semibold uppercase tracking-[0.2em] text-terracotta">Catalog</p>
      <h1 className="mt-2 font-display text-4xl font-light tracking-tight text-charcoal md:text-5xl">Color Governance</h1>
      <p className="mt-3 max-w-2xl text-sm leading-relaxed text-warmgrey">
        Govern the mapping between each master color (001–033) and its approved names, families, aliases and descriptors.
        Only published (Approved) mappings propagate to the Dealer catalog, product pages, search and ZELI. Drafts and
        unresolved fields never become ZELI truth. Click a row to edit it in place; click another row to switch directly.
      </p>
      <BatchReview onDone={load} />

      <div className="mt-8 overflow-x-auto rounded-md border border-border bg-white">
        <table className="w-full text-sm" data-testid="governance-colors-table">
          <thead>
            <tr className="border-b border-border text-left text-xs uppercase tracking-widest text-muted-foreground">
              <th className="px-4 py-3">Code</th><th className="px-4 py-3">Factory name <span className="sm:hidden">/ U.S. name</span></th>
              <th className="hidden px-4 py-3 sm:table-cell">Approved US name</th><th className="px-4 py-3">Governance</th><th className="hidden px-4 py-3 sm:table-cell">Rev</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <Fragment key={r.code}>
                <tr onClick={() => toggle(r.code)} data-testid={`gov-row-${r.code}`}
                  className={`cursor-pointer border-b border-border transition-colors hover:bg-sand ${sel?.color.code === r.code ? "bg-sand" : ""}`}>
                  <td className="px-4 py-2.5 font-mono text-xs font-semibold">{r.code}</td>
                  <td className="px-4 py-2.5">
                    {r.name}
                    <span className="block text-xs sm:hidden" data-testid={`gov-us-mobile-${r.code}`}>
                      {r.governance_status === "APPROVED" && r.approved_us_name ? <span className="font-semibold text-terracotta">{r.approved_us_name}</span> : <span className="italic text-warmgrey">U.S. name pending</span>}
                    </span>
                  </td>
                  <td className="hidden px-4 py-2.5 sm:table-cell">{r.approved_us_name || <span className="text-xs italic text-warmgrey">Unresolved</span>}</td>
                  <td className="px-4 py-2.5">
                    <span className={`rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase ${STATUS_BADGE[r.governance_status] || "badge-reserved"}`}
                      data-testid={`gov-status-${r.code}`}>{r.governance_status}</span>
                  </td>
                  <td className="hidden px-4 py-2.5 font-mono text-xs sm:table-cell">{r.effective_revision || 0}</td>
                </tr>
                {sel?.color.code === r.code && form && (
                  <tr data-testid={`gov-editor-row-${r.code}`}>
                    <td colSpan={5} className="border-b border-border bg-sand/50 px-4 py-4">
                      {/* RC48-MOB-015: the editor sits in a sticky, viewport-wide cell so the
                          primary action never scrolls off-screen with the wide table */}
                      <div className="sticky left-0 w-[calc(100vw-4rem)] max-w-full sm:w-auto">{renderEditor()}</div>
                    </td>
                  </tr>
                )}
              </Fragment>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
